import { takeEvery } from 'redux-saga';
import {
    call,
    put,
    take,
    race,
    select
} from 'redux-saga/effects';
import { parse, extract } from 'query-string';

import {
    getMislStore,
    getMislStores
} from '../../../core/js/sagas/mislSagas';

import { saveDefaultUrbnStore,
    updateDefaultUrbnStorePools,
    SAVE_DEFAULT_URBN_STORE,
    UPDATE_DEFAULT_URBN_STORE_POOLS
} from '../../../core/js/actions/bopisActions';

import {
    saveBopisPools,
    toggleNoStoreMessage,
    updateBopisStockInfo,
    SAVE_BOPIS_POOLS,
    UPDATE_SELECTED_SKU
} from '../actions/productActions';

import { makeRequest } from '../../../core/js/sagas/makeRequest';

import {
    getBopisPoolRoute,
    getProfilePreferences
} from '../../../core/js/utils/apiRoutes';

import {
    setLocalStorageItemWithFallback,
    getLocalStorageItemWithFallback
} from '../../../core/js/utils/cacheUtils';

import {
    buildQueryObjForProduct,
    getTargetedProduct,
    getBopisStockInfo
} from '../utils/utils';

import { isQuickshop } from '../utils/quickshopUtils';

import { DEFAULT_STORE_KEY } from '../enums/bopisEnums';

// UW-12621 Remove when marketplace services are ready
import { getParameterByName } from '../../../core/js/utils';
import { getLoggedInState } from '../../../core/js/utils/authUtils';

/* eslint-disable complexity */
/**
 * Handle fetching of the default URBN store from MISL
 * @param  {string}  poolId   Store Id.
 * @param  {object}     meta     Meta info about the type of request
 *
 * poolId will be set from Quickshop if a poolId has already been
 *  read (either from the cache or from a loaded product in cart).  If this is
 *  set, the call to updateDefaultUrbnStorePools is not needed.  The initial
 *  product request in quickshop already pulled the pools
 *
 */
export function *handleDefaultUrbnStoreSaga(poolId, meta) {
    const state = yield select();

    // Pull includePools param from URL rather than
    // store since we intentionally exclude it from
    // the store for all calls except to orchestration
    const qs = extract(window.location.href);
    const queryParams = parse(qs);
    const userLocation = state.core.userLocation;

    let defaultStore = yield call(getDefaultStoreSaga);

    if (isQuickshop(state) && poolId) {
        // Could be loading up a product from cart.  Need to check the inventory pool
        // *but* don't save this as the default store

        if (defaultStore && defaultStore.storeNumber === poolId) {
            // Same as the cached store.  Nothing to do
            yield put(saveDefaultUrbnStore(defaultStore, meta));
        } else {
            yield call(getMislStore, {
                storeNumber: poolId
            }, meta);
        }
    } else if (queryParams.includePools) {
        // When there is an includePools override then fetch a new store
        yield call(getMislStore, {
            storeNumber: queryParams.includePools
        }, meta);

        defaultStore = getLocalStorageItemWithFallback(DEFAULT_STORE_KEY);
        yield put(updateDefaultUrbnStorePools(defaultStore, meta));
    } else if (defaultStore && defaultStore.storeNumber) {
        // If there is a default store and it has a store number use it instead of fetching a new one
        yield put(saveDefaultUrbnStore(defaultStore, meta));
        yield put(updateDefaultUrbnStorePools(defaultStore, meta));
    } else if (userLocation && (userLocation.lat || userLocation.long)) {
        // If there is no override and there is no saved default store
        // and we have a user location we fetch a new one
        yield call(getMislStore,
            {
                distance: 200,
                lat: userLocation.lat,
                lng: userLocation.long
            },
            meta
        );

        defaultStore = getLocalStorageItemWithFallback(DEFAULT_STORE_KEY);
        yield put(updateDefaultUrbnStorePools(defaultStore, meta));
    } else {
        // If there is no save store, override, or akamai location, message the
        // user to search for a store
        yield put(toggleNoStoreMessage(true, meta));
    }
}
/* eslint-enable complexity */

export function *getDefaultStoreSaga() {
    const cachedStore = getLocalStorageItemWithFallback(DEFAULT_STORE_KEY);

    const homeStoreNumber = yield call(getHomeStoreNumberSaga);
    const homeStoreIsCached = (homeStoreNumber === _.get(cachedStore, 'storeNumber', null));

    if (homeStoreNumber && !homeStoreIsCached) {
        const mislResponse = yield call(getMislStores, { storeNumber: homeStoreNumber });
        return _.get(mislResponse, 'results[0]', null);
    }

    return cachedStore;
}

export function *getHomeStoreNumberSaga() {
    const state = yield select();
    const userIsLoggedIn = getLoggedInState();

    let homeStoreNumber = _.get(state, 'core.profile.webPreferences.favoriteStores[0]', null);

    if (!homeStoreNumber && userIsLoggedIn) {
        const route = getProfilePreferences(state);
        const res = yield call(makeRequest, route);

        homeStoreNumber = _.get(res, 'favoriteStores[0]', null);
    }

    return homeStoreNumber;
}

export function *saveDefaultUrbnStoreSaga(action) {
    if (action.payload.productBopis.defaultStore) {
        const twoMonthsInSeconds = 5184000;
        setLocalStorageItemWithFallback(DEFAULT_STORE_KEY, action.payload.productBopis.defaultStore, twoMonthsInSeconds);
    }
}

export function *handleSaveDefaultStoreSaga() {
    yield* takeEvery(SAVE_DEFAULT_URBN_STORE, saveDefaultUrbnStoreSaga);
}

export function *updateBopisPoolsSaga(action) {
    const state = yield select();
    const payload = action.payload;
    const meta = action.meta;

    if (payload.productBopis.defaultStore) {
        // Only refetch product orchestration if we have a default store
        const productState = getTargetedProduct(state);
        const qs = buildQueryObjForProduct(productState, {});

        // For marketplace mock data: if params are present, include in BOPIS call
        // UW-12621 Remove when marketplace services are ready
        const marketplaceQuery = getParameterByName('marketplace');
        const minShippingQuery = getParameterByName('minShippingPrice');
        const logisticClassQuery = getParameterByName('logisticClass');
        const marketplace = marketplaceQuery ? { marketplace: true } : {};
        const minShippingPrice = minShippingQuery ? { minShippingPrice: minShippingQuery } : {};
        const logisticClass = logisticClassQuery ? { logisticClass: logisticClassQuery } : {};

        const queryParams = _.merge(
            qs,
            {
                includePools: payload.productBopis.defaultStore.storeNumber,
                trim: true,
                productId: productState.productId,
                'exclude-filter': ''
            },
            marketplace,
            minShippingPrice,
            logisticClass
        );

        // eslint-disable-next-line
        const { updateBopisPoolsResponse, userUpdatedSku } = yield race({
            updateBopisPoolsResponse: call(makeRequest, getBopisPoolRoute(state, queryParams)),
            userUpdatedSku: take(UPDATE_SELECTED_SKU)
        });

        if (updateBopisPoolsResponse) {
            yield put(saveBopisPools(updateBopisPoolsResponse));
        }
    } else {
        // If no default store (meaning MISL returned nothing) message
        // user to select a store
        yield put(toggleNoStoreMessage(true, meta));
    }
}

export function *handleUpdateDefaultStorePoolsSaga() {
    yield* takeEvery(UPDATE_DEFAULT_URBN_STORE_POOLS, updateBopisPoolsSaga);
}

export function *updateBopisStockInfosSaga(action) {
    const state = yield select();
    const product = getTargetedProduct(state);
    const skuObj = _.get(product, 'selectedSku.sku');

    if (skuObj) {
        const bopisStockInfo = getBopisStockInfo(state, skuObj);
        yield put(updateBopisStockInfo(bopisStockInfo));
    }
}

export function *handleUpdateBopisStockInfoSaga() {
    yield* takeEvery(SAVE_BOPIS_POOLS, updateBopisStockInfosSaga);
}

export default [
    handleSaveDefaultStoreSaga,
    handleUpdateDefaultStorePoolsSaga,
    handleUpdateBopisStockInfoSaga
];
